<template>
    <el-dialog
        :visible="visible"
        :title="dialogTitle"
        width="600px"
        custom-class="rcv-dialog rcv-work-confirm-dialog"
        :close-on-click-modal="false"
        append-to-body
        @update:visible="handleClose"
    >
        <div v-if="candidate" class="rcv-work-confirm">
            <!-- Cảnh báo nếu không thuộc phòng nhân sự -->
            <el-alert
                v-if="!isHrDepartment"
                title="Quyền hạn bị giới hạn"
                type="error"
                description="Chỉ nhân sự thuộc phòng Nhân sự mới có quyền xác nhận ứng viên đi làm / không đi làm."
                show-icon
                :closable="false"
                class="mb-3"
            />

            <!-- Thẻ tóm tắt thông tin ứng viên -->
            <div class="rcv-work-confirm__cand-card">
                <div class="rcv-work-confirm__cand-head">
                    <div class="rcv-work-confirm__avatar">
                        <i class="el-icon-user-solid"></i>
                    </div>
                    <div class="rcv-work-confirm__cand-info">
                        <div class="rcv-work-confirm__cand-name-row">
                            <span class="rcv-work-confirm__cand-name">
                                {{ candidate.name || ("Ứng viên #" + candidate.id) }}
                            </span>
                            <span class="rcv-work-confirm__cand-id">#{{ candidate.id }}</span>
                            <el-tag size="mini" type="success" effect="dark">
                                <i class="el-icon-document-checked"></i> Cần làm thủ tục Pass (#14)
                            </el-tag>
                        </div>
                        <div class="rcv-work-confirm__cand-meta">
                            <span v-if="candidateJobTitle"><i class="el-icon-suitcase"></i> {{ candidateJobTitle }}</span>
                            <span v-if="candidate.phone"><i class="el-icon-phone-outline"></i> {{ candidate.phone }}</span>
                            <span v-if="candidate.email"><i class="el-icon-message"></i> {{ candidate.email }}</span>
                        </div>
                    </div>
                </div>

                <!-- Tóm tắt thông tin Offer đã điền trước đó -->
                <div v-if="hasOfferSummary" class="rcv-work-confirm__offer-summary">
                    <div v-if="candidateSalary" class="rcv-work-confirm__offer-item">
                        <span class="rcv-offer-item__label">Mức lương:</span>
                        <span class="rcv-offer-item__val text-success font-bold">{{ formatMoney(candidateSalary) }} VNĐ</span>
                    </div>
                    <div v-if="candidateStartDate" class="rcv-work-confirm__offer-item">
                        <span class="rcv-offer-item__label">Ngày thử việc:</span>
                        <span class="rcv-offer-item__val text-primary font-bold">{{ formatDate(candidateStartDate) }}</span>
                    </div>
                    <div v-if="candidate.probation_end_date" class="rcv-work-confirm__offer-item">
                        <span class="rcv-offer-item__label">Kết thúc thử việc:</span>
                        <span class="rcv-offer-item__val text-indigo font-bold">{{ formatDate(candidate.probation_end_date) }}</span>
                    </div>
                </div>
            </div>

            <!-- Form Xác nhận Đi làm / Không đi làm -->
            <el-form
                ref="formRef"
                :model="form"
                :rules="rules"
                label-position="top"
                size="small"
                class="rcv-work-confirm-form"
                @submit.native.prevent="handleSubmit"
            >
                <!-- Thẻ lựa chọn: Có đi làm / Không đi làm -->
                <el-form-item label="Xác nhận trạng thái nhận việc của ứng viên" required>
                    <div class="rcv-choice-cards">
                        <!-- Lựa chọn 1: Có đi làm -->
                        <div
                            class="rcv-choice-card rcv-choice-card--work"
                            :class="{ 'is-active': form.has_work === true }"
                            @click="setHasWork(true)"
                        >
                            <div class="rcv-choice-card__icon">
                                <i class="el-icon-circle-check"></i>
                            </div>
                            <div class="rcv-choice-card__body">
                                <div class="rcv-choice-card__title">CÓ ĐI LÀM</div>
                                <div class="rcv-choice-card__desc">
                                    Ứng viên đến nhận việc đúng hạn, chuyển sang <b>Đã gửi đào tạo hội nhập (#16)</b>
                                </div>
                            </div>
                            <div class="rcv-choice-card__radio">
                                <i :class="form.has_work === true ? 'el-icon-success' : 'el-icon-circle-check'"></i>
                            </div>
                        </div>

                        <!-- Lựa chọn 2: Không đi làm -->
                        <div
                            class="rcv-choice-card rcv-choice-card--no-work"
                            :class="{ 'is-active': form.has_work === false }"
                            @click="setHasWork(false)"
                        >
                            <div class="rcv-choice-card__icon">
                                <i class="el-icon-circle-close"></i>
                            </div>
                            <div class="rcv-choice-card__body">
                                <div class="rcv-choice-card__title">KHÔNG ĐI LÀM</div>
                                <div class="rcv-choice-card__desc">
                                    Ứng viên từ chối / không đến nhận việc, chuyển sang <b>Ứng viên không đi làm (#15)</b>
                                </div>
                            </div>
                            <div class="rcv-choice-card__radio">
                                <i :class="form.has_work === false ? 'el-icon-error' : 'el-icon-circle-close'"></i>
                            </div>
                        </div>
                    </div>
                </el-form-item>

                <!-- Trường hợp: KHÔNG ĐI LÀM -> Bắt buộc nhập lý do (probation_reason) -->
                <transition name="el-fade-in-linear">
                    <div v-if="form.has_work === false" class="rcv-no-work-section">
                        <el-form-item label="Lý do ứng viên không đi làm" prop="probation_reason" required>
                            <el-input
                                v-model="form.probation_reason"
                                type="textarea"
                                :rows="3"
                                placeholder="Nhập lý do ứng viên không đi làm (VD: Ứng viên đổi ý, đã nhận offer công ty khác, không liên lạc được, bận việc gia đình...)..."
                                maxlength="1000"
                                show-word-limit
                            />
                        </el-form-item>

                        <!-- Gợi ý nhanh các lý do phổ biến -->
                        <div class="rcv-reason-tags mb-3">
                            <span class="rcv-reason-tags__label">Gợi ý nhanh:</span>
                            <el-tag
                                v-for="tag in commonReasons"
                                :key="tag"
                                size="mini"
                                effect="plain"
                                class="rcv-reason-tag"
                                @click="addReason(tag)"
                            >
                                + {{ tag }}
                            </el-tag>
                        </div>

                        <div class="rcv-no-work-notice">
                            <i class="el-icon-warning-outline"></i>
                            <span>
                                Hồ sơ sẽ được chuyển sang trạng thái <b>Ứng viên không đi làm (#15)</b> và dừng tiến trình ứng tuyển. Lý do này sẽ được lưu vào thông tin thử việc của ứng viên.
                            </span>
                        </div>
                    </div>
                </transition>

                <!-- Trường hợp: CÓ ĐI LÀM -> Chuyển sang Đã gửi đào tạo hội nhập (16) -->
                <transition name="el-fade-in-linear">
                    <div v-if="form.has_work === true" class="rcv-work-section">
                        <div class="rcv-work-notice mb-3">
                            <div class="rcv-work-notice__icon">
                                <i class="el-icon-reading"></i>
                            </div>
                            <div class="rcv-work-notice__content">
                                <div class="rcv-work-notice__title">
                                    Chuyển sang: Đã gửi đào tạo hội nhập (#16)
                                </div>
                                <div class="rcv-work-notice__desc">
                                    Ứng viên sẽ được tiếp nhận chính thức và chuyển sang danh sách <b>Đào tạo hội nhập</b> để xếp lớp và chuẩn bị các khóa đào tạo nội bộ.
                                </div>
                            </div>
                        </div>

                        <el-form-item label="Ghi chú tiếp nhận (Tuỳ chọn)">
                            <el-input
                                v-model="form.note"
                                type="textarea"
                                :rows="2"
                                placeholder="Nhập ghi chú thêm về buổi nhận việc của ứng viên (tuỳ chọn)..."
                                maxlength="1000"
                                show-word-limit
                            />
                        </el-form-item>
                    </div>
                </transition>
            </el-form>
        </div>

        <span slot="footer" class="dialog-footer">
            <el-button size="small" @click="handleClose">Đóng</el-button>
            <el-button
                size="small"
                :type="form.has_work === false ? 'danger' : 'success'"
                :loading="submitting"
                :disabled="!isHrDepartment"
                :icon="form.has_work === false ? 'el-icon-close' : 'el-icon-check'"
                @click="handleSubmit"
            >
                {{ form.has_work === false ? 'Xác nhận Không đi làm (#15)' : 'Xác nhận Có đi làm -> Đã gửi đào tạo hội nhập (#16)' }}
            </el-button>
        </span>
    </el-dialog>
</template>

<script>
import moment from "moment-timezone";
import {
    CAN_LAM_THU_TUC_PASS,
    UNG_VIEN_KHONG_DI_LAM,
    DA_GUI_DAO_TAO_HOI_NHAP,
} from "@/core/config/recruitmentCvs/recruitmentCvsOption";

export default {
    name: "WorkConfirmationDialog",
    props: {
        visible: { type: Boolean, default: false },
        candidate: { type: Object, default: null },
        orders: { type: Array, default: () => [] },
        isHrDepartment: { type: Boolean, default: true },
        submitting: { type: Boolean, default: false },
        initialHasWork: { type: Boolean, default: true },
    },
    data() {
        return {
            form: {
                has_work: true,
                probation_reason: "",
                note: "",
            },
            commonReasons: [
                "Ứng viên nhận offer công ty khác",
                "Ứng viên đổi ý không muốn thay đổi công việc",
                "Không liên lạc được với ứng viên",
                "Ứng viên có việc gia đình đột xuất",
                "Khoảng cách địa lý đi lại quá xa",
            ],
            rules: {
                probation_reason: [
                    {
                        validator: (rule, value, callback) => {
                            if (this.form.has_work === false && (!value || !value.trim())) {
                                callback(new Error("Vui lòng nhập Lý do ứng viên không đi làm"));
                                return;
                            }
                            callback();
                        },
                        trigger: ["blur", "change"],
                    },
                ],
            },
        };
    },
    computed: {
        dialogTitle() {
            return "Xác nhận ứng viên đi làm (Cần làm thủ tục Pass)";
        },
        candidateJobTitle() {
            if (!this.candidate) return "";
            const c = this.candidate;
            const found = this.orders.find((o) =>
                String(o.id) === String(c.job_id) ||
                String(o.id) === String(c.order_id) ||
                String(o.id) === String(c.raw_job_id) ||
                (o.recruitment_job_id && String(o.recruitment_job_id) === String(c.job_id)) ||
                (o.recruitment_job_id && String(o.recruitment_job_id) === String(c.raw_job_id))
            );
            return found ? (found.title || found.position_title || "") : (c.order_title || "");
        },
        candidateSalary() {
            if (!this.candidate) return null;
            return this.candidate.salary || this.candidate.expected_salary || null;
        },
        candidateStartDate() {
            if (!this.candidate) return "";
            return this.candidate.probation_start_date || this.candidate.start_date || this.candidate.probation_date || "";
        },
        hasOfferSummary() {
            return Boolean(this.candidateSalary || this.candidateStartDate || (this.candidate && this.candidate.probation_end_date));
        },
    },
    watch: {
        visible(val) {
            if (val) {
                this.initForm();
            }
        },
        candidate() {
            if (this.visible) {
                this.initForm();
            }
        },
    },
    methods: {
        formatMoney(num) {
            if (num === null || num === undefined || num === "") return "0";
            return Number(num).toLocaleString("vi-VN");
        },
        formatDate(val) {
            if (!val) return "—";
            return moment(val).format("DD/MM/YYYY");
        },
        initForm() {
            const c = this.candidate || {};
            const isNoWorkCurrently = Number(c.status) === UNG_VIEN_KHONG_DI_LAM;
            this.form = {
                has_work: isNoWorkCurrently ? false : this.initialHasWork,
                probation_reason: c.probation_reason || "",
                note: "",
            };
            this.$nextTick(() => {
                if (this.$refs.formRef) {
                    this.$refs.formRef.clearValidate();
                }
            });
        },
        setHasWork(val) {
            this.form.has_work = val;
            this.$nextTick(() => {
                if (this.$refs.formRef) {
                    this.$refs.formRef.clearValidate();
                }
            });
        },
        addReason(tag) {
            if (!this.form.probation_reason) {
                this.form.probation_reason = tag;
            } else if (!this.form.probation_reason.includes(tag)) {
                this.form.probation_reason = `${this.form.probation_reason}; ${tag}`;
            }
            if (this.$refs.formRef) {
                this.$refs.formRef.validateField("probation_reason");
            }
        },
        handleClose() {
            this.$emit("update:visible", false);
        },
        handleSubmit() {
            if (!this.candidate) return;
            if (!this.isHrDepartment) {
                this.$message.warning("Chỉ nhân sự thuộc phòng Nhân sự mới có quyền xác nhận");
                return;
            }

            this.$refs.formRef.validate((valid) => {
                if (!valid) return;

                const hasWork = this.form.has_work === true;
                const targetStatus = hasWork ? DA_GUI_DAO_TAO_HOI_NHAP : UNG_VIEN_KHONG_DI_LAM; // 16 hoặc 15
                const reason = (this.form.probation_reason || "").trim();
                const note = (this.form.note || "").trim();

                let noteContent = hasWork
                    ? `[Xác nhận đi làm - Đã gửi đào tạo hội nhập (#16)]`
                    : `[Ứng viên không đi làm (#15)]`;

                if (!hasWork && reason) {
                    noteContent += `\nLý do không đi làm: ${reason}`;
                }
                if (hasWork && note) {
                    noteContent += `\nGhi chú: ${note}`;
                }

                const payload = {
                    id: this.candidate.id,
                    status: targetStatus,
                    has_work: hasWork,
                    probation_reason: !hasWork ? reason : null,
                    reason: !hasWork ? reason : undefined,
                    note: noteContent,
                    message: noteContent,
                    candidate: {
                        ...this.candidate,
                        status: targetStatus,
                        probation_reason: !hasWork ? reason : null,
                    },
                };

                this.$emit("submit", payload);
            });
        },
    },
};
</script>

<style scoped>
.rcv-work-confirm {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

/* Thẻ thông tin ứng viên */
.rcv-work-confirm__cand-card {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 14px 16px;
}
.rcv-work-confirm__cand-head {
    display: flex;
    align-items: center;
    gap: 12px;
}
.rcv-work-confirm__avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: #e0f2fe;
    color: #0284c7;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    flex-shrink: 0;
}
.rcv-work-confirm__cand-info {
    flex: 1;
    min-width: 0;
}
.rcv-work-confirm__cand-name-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 4px;
}
.rcv-work-confirm__cand-name {
    font-size: 15px;
    font-weight: 700;
    color: #1e293b;
}
.rcv-work-confirm__cand-id {
    font-size: 12px;
    color: #64748b;
    font-weight: 600;
}
.rcv-work-confirm__cand-meta {
    display: flex;
    align-items: center;
    gap: 14px;
    font-size: 12.5px;
    color: #64748b;
    flex-wrap: wrap;
}
.rcv-work-confirm__cand-meta i {
    color: #94a3b8;
    margin-right: 2px;
}

/* Tóm tắt Offer */
.rcv-work-confirm__offer-summary {
    display: flex;
    align-items: center;
    gap: 18px;
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px dashed #cbd5e1;
    font-size: 12.5px;
    flex-wrap: wrap;
}
.rcv-work-confirm__offer-item {
    display: flex;
    align-items: center;
    gap: 6px;
}
.rcv-offer-item__label {
    color: #64748b;
}
.rcv-offer-item__val {
    font-weight: 600;
}

/* Thẻ lựa chọn: Có đi làm / Không đi làm */
.rcv-choice-cards {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}
.rcv-choice-card {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s ease;
    background: #fff;
    user-select: none;
}
.rcv-choice-card:hover {
    border-color: #cbd5e1;
    transform: translateY(-1px);
}
.rcv-choice-card__icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;
}
.rcv-choice-card__body {
    flex: 1;
    min-width: 0;
}
.rcv-choice-card__title {
    font-size: 13.5px;
    font-weight: 700;
    margin-bottom: 2px;
}
.rcv-choice-card__desc {
    font-size: 11.5px;
    color: #64748b;
    line-height: 1.35;
}
.rcv-choice-card__radio {
    font-size: 18px;
    color: #cbd5e1;
    flex-shrink: 0;
}

/* Card: Có đi làm */
.rcv-choice-card--work .rcv-choice-card__icon {
    background: #ecfdf5;
    color: #10b981;
}
.rcv-choice-card--work.is-active {
    border-color: #10b981;
    background: #f0fdf4;
    box-shadow: 0 0 0 1px #10b981;
}
.rcv-choice-card--work.is-active .rcv-choice-card__title {
    color: #059669;
}
.rcv-choice-card--work.is-active .rcv-choice-card__radio {
    color: #10b981;
}

/* Card: Không đi làm */
.rcv-choice-card--no-work .rcv-choice-card__icon {
    background: #fef2f2;
    color: #ef4444;
}
.rcv-choice-card--no-work.is-active {
    border-color: #ef4444;
    background: #fef2f2;
    box-shadow: 0 0 0 1px #ef4444;
}
.rcv-choice-card--no-work.is-active .rcv-choice-card__title {
    color: #dc2626;
}
.rcv-choice-card--no-work.is-active .rcv-choice-card__radio {
    color: #ef4444;
}

/* Gợi ý tags */
.rcv-reason-tags {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}
.rcv-reason-tags__label {
    font-size: 11.5px;
    color: #64748b;
    margin-right: 2px;
}
.rcv-reason-tag {
    cursor: pointer;
    border-radius: 4px;
    font-size: 11px;
    transition: all 0.15s ease;
}
.rcv-reason-tag:hover {
    background-color: #fee2e2;
    color: #dc2626;
    border-color: #fca5a5;
}

/* Notice box: Không đi làm */
.rcv-no-work-notice {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    background: #fff7ed;
    border: 1px dashed #fed7aa;
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 12px;
    color: #9a3412;
    line-height: 1.45;
}
.rcv-no-work-notice i {
    font-size: 16px;
    margin-top: 2px;
    color: #ea580c;
}

/* Notice box: Có đi làm */
.rcv-work-notice {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    background: #f0fdf4;
    border: 1.5px solid #bbf7d0;
    border-radius: 10px;
    padding: 12px 14px;
}
.rcv-work-notice__icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: #dcfce7;
    color: #16a34a;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;
}
.rcv-work-notice__content {
    flex: 1;
    min-width: 0;
}
.rcv-work-notice__title {
    font-size: 13.5px;
    font-weight: 700;
    color: #15803d;
    margin-bottom: 2px;
}
.rcv-work-notice__desc {
    font-size: 12px;
    color: #475569;
    line-height: 1.4;
}

@media (max-width: 576px) {
    .rcv-choice-cards {
        grid-template-columns: 1fr;
    }
}
</style>
